import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiTabs } from 'noddi-ui-web';

import { BulkStrategy, PickerMode } from '../../../../stores/tireOfferPickerStore';
import { getTabsTranslations } from '../../../../utils/commonTranslations';

type ModeToggleProps = {
  mode: PickerMode;
  onChange: (mode: PickerMode) => void;
};

export const ModeToggle = ({ mode, onChange }: ModeToggleProps) => (
  <div className='inline-flex items-center gap-2'>
    <span className='text-[11px] font-semibold tracking-wider text-muted-foreground uppercase'>
      <Trans>Pick mode</Trans>
    </span>
    <NoddiTabs<PickerMode>
      translations={getTabsTranslations()}
      defaultValue='manual'
      value={mode}
      onValueChange={onChange}
      tabItems={[
        { label: t`Auto-pick`, value: 'auto', icon: 'WandSparkles', node: null },
        { label: t`Manual`, value: 'manual', icon: 'Edit', node: null }
      ]}
      contentClassName='hidden'
    />
  </div>
);

type ModeBannerProps = {
  mode: PickerMode;
  strategy: BulkStrategy;
  onStrategyChange: (strategy: BulkStrategy) => void;
};

function getStrategyOptions(): { value: BulkStrategy; label: string; icon?: 'Star' }[] {
  return [
    { value: 'cheapest', label: t`Cheapest` },
    { value: 'mid', label: t`Standard` },
    { value: 'premium', label: t`Premium` },
    { value: 'recommended', label: t`Recommended`, icon: 'Star' }
  ];
}

function getStrategyDescription(strategy: BulkStrategy): string {
  switch (strategy) {
    case 'cheapest':
      return t`cheapest option`;
    case 'mid':
      return t`standard option`;
    case 'premium':
      return t`premium option`;
    case 'recommended':
      return t`recommended option (Standard if none)`;
  }
}

export const ModeBanner = ({ mode, strategy, onStrategyChange }: ModeBannerProps) => {
  if (mode === 'auto') {
    const strategyDescription = getStrategyDescription(strategy);

    return (
      <div className='flex flex-col gap-3 rounded-lg border border-border bg-white p-4 md:flex-row md:items-center'>
        <div className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-card'>
          <NoddiIcon name='WandSparkles' size='small' />
        </div>
        <div className='flex-1'>
          <div className='text-sm font-semibold'>
            <Trans>Auto-pick mode</Trans>
          </div>
          <div className='text-xs text-muted-foreground'>
            <Trans>
              Selecting the {strategyDescription} for every offer. Switch to{' '}
              <strong className='text-foreground'>Manual</strong> to choose per row.
            </Trans>
          </div>
        </div>
        <div className='-mx-4 flex flex-nowrap gap-1 overflow-x-auto rounded-none border-y border-border bg-card p-0.5 px-4 md:mx-0 md:inline-flex md:overflow-visible md:rounded-lg md:border md:px-0.5'>
          {getStrategyOptions().map((option) => {
            const isActive = strategy === option.value;
            return (
              <button
                key={option.value}
                type='button'
                onClick={() => onStrategyChange(option.value)}
                className={cn(
                  'inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-3 text-xs transition-colors',
                  isActive
                    ? 'bg-card-selected-background font-semibold text-card-selected-foreground'
                    : 'font-medium text-muted-foreground hover:text-primary'
                )}
              >
                {option.icon && <NoddiIcon name={option.icon} size='xSmall' />}
                {option.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className='flex items-center gap-3 rounded-lg border border-border bg-card p-4'>
      <div className='flex size-8 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-primary'>
        <NoddiIcon name='Edit' size='small' />
      </div>
      <div className='flex-1'>
        <div className='text-sm font-semibold text-foreground'>
          <Trans>Manual pick mode</Trans>
        </div>
        <div className='text-xs text-muted-foreground'>
          <Trans>
            Click any option in a row to select it. Switch to <strong className='text-primary'>Auto-pick</strong> to
            apply a strategy to all rows at once.
          </Trans>
        </div>
      </div>
    </div>
  );
};
